import { useEffect, useRef } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

const SCAN_GAP_MS = 100;
const FIELD_SCAN_GAP_MS = 40;
const FIELD_MIN_LEN = 8;
const OPEN_MIN_LEN = 4;

/** Local USB scanner bridge — `scripts/pos-scanner-bridge.py` (showroom + admin). */
export const POS_SCANNER_BRIDGE_SSE_URL = 'http://127.0.0.1:3921/stream';

function stripBurst(el: HTMLInputElement | HTMLTextAreaElement, burst: string) {
  if (!el.value.endsWith(burst)) return;
  const proto =
    el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, el.value.slice(0, -burst.length));
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * HID keyboard-wedge + optional local SSE bridge (Linux scanners without key events).
 * Same thresholds as showroom POS.
 */
export function useHidBarcodeWedge(opts: {
  enabled: boolean;
  onScan: (code: string) => void;
  /** Skip global handler when this field owns Enter (handles scan itself). */
  dedicatedInputId?: string;
  /** `pos-scanner-bridge.py` on 127.0.0.1:3921 — default on when enabled. */
  scannerBridge?: boolean;
}) {
  const onScanRef = useRef(opts.onScan);
  onScanRef.current = opts.onScan;
  const dedicatedId = opts.dedicatedInputId;
  const useBridge = opts.scannerBridge !== false;

  useEffect(() => {
    if (!opts.enabled) return;

    let buffer = '';
    let lastKeyTime = 0;
    let lastTarget: EventTarget | null = null;

    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      const isField = tag === 'INPUT' || tag === 'TEXTAREA';
      if (tag === 'SELECT') return;
      if (dedicatedId && el?.id === dedicatedId) return;

      const now = Date.now();
      if (now - lastKeyTime > (isField ? FIELD_SCAN_GAP_MS : SCAN_GAP_MS) || e.target !== lastTarget) {
        buffer = '';
      }
      lastKeyTime = now;
      lastTarget = e.target;

      if (e.key === 'Enter') {
        if (buffer.length >= (isField ? FIELD_MIN_LEN : OPEN_MIN_LEN)) {
          e.preventDefault();
          e.stopPropagation();
          if (isField) stripBurst(el as HTMLInputElement | HTMLTextAreaElement, buffer);
          onScanRef.current(buffer);
        }
        buffer = '';
        return;
      }

      const numpadDigit = /^Numpad([0-9])$/.exec(e.code)?.[1];
      if (numpadDigit) buffer += numpadDigit;
      else if (e.key.length === 1) buffer += e.key;
    };

    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [opts.enabled, dedicatedId]);

  useEffect(() => {
    if (!opts.enabled || !useBridge) return;

    let es: EventSource | null = null;
    let reconnectTimer: number | null = null;
    let cancelled = false;
    let attempt = 0;

    const connect = () => {
      if (cancelled) return;
      es = new EventSource(POS_SCANNER_BRIDGE_SSE_URL);
      es.onopen = () => {
        attempt = 0;
      };
      es.onmessage = (ev) => {
        const code = ev.data?.trim();
        if (code) onScanRef.current(code);
      };
      es.onerror = () => {
        es?.close();
        es = null;
        if (cancelled) return;
        attempt += 1;
        const delay = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5));
        reconnectTimer = window.setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      cancelled = true;
      es?.close();
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
    };
  }, [opts.enabled, useBridge]);
}

/** Append Numpad digit from `code` when Num Lock is off (Linux HID scanners). */
export function appendNumpadDigitFromCode(e: ReactKeyboardEvent<HTMLInputElement>): boolean {
  const numpadDigit = /^Numpad([0-9])$/.exec(e.code)?.[1];
  if (!numpadDigit) return false;
  e.preventDefault();
  const el = e.currentTarget;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const next = el.value.slice(0, start) + numpadDigit + el.value.slice(end);
  const proto = HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, next);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  const caret = start + 1;
  el.setSelectionRange(caret, caret);
  return true;
}

export const CATALOG_CREATE_WIZARD_NAME_INPUT_ID = 'catalog-create-wizard-name';
